/** HTTP status off an axios-style error, when there is one. */
export function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } } | null)?.response?.status
}

export function dossierLoadMessage(err: unknown, _loanId?: string): string {
  const status = httpStatus(err)
  if (status === 401) return 'Sign in to load this deal dossier.'
  if (status === 403) {
    return 'You do not have permission to view this deal record. Ask an administrator for pipeline access.'
  }
  if (status === 404) {
    return 'This deal is not in the pipeline book, and no deal file was returned.'
  }
  return 'This deal dossier could not be loaded. Nothing has been changed.'
}

export function timelineLoadMessage(err: unknown): string {
  const status = httpStatus(err)
  if (status === 401) return 'Sign in to load the stage timeline.'
  if (status === 403) return 'You do not have permission to view this deal’s stage timeline.'
  if (status === 404) return 'No stage history recorded for this deal yet.'
  return 'The stage timeline could not be loaded.'
}

export function mapsConfigLoadMessage(err: unknown): string {
  const status = httpStatus(err)
  if (status === 401) return 'Sign in to load the Maps key from settings.'
  if (status === 403) return 'This account cannot read Maps settings.'
  if (status === 404) {
    return 'Maps settings endpoint was not found. The API host may be out of date.'
  }
  if (status) return `Could not read the Maps key from settings (${status}).`
  return 'Could not read the Maps key from settings.'
}

/**
 * Why a deal save was refused, in a sentence a person can act on.
 *
 * FastAPI answers a rejected write with `detail: [{loc, msg, input}, …]`, and
 * nothing was reading it — a 422 left the form open, the button back on "Save
 * changes", and no message anywhere on the page. The field name is the part
 * that matters: "Property: String should have at least 1 character" says what
 * to go and fix, where "Save failed" does not.
 */
export function loanSaveMessage(err: unknown): string {
  const status = httpStatus(err)
  const detail = (err as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail
  if (status === 401) return 'Sign in again — your session expired before the change was saved.'
  if (status === 403) {
    // A policy refusal (the admin-only stage gate) says what to do next; a bare
    // RBAC denial ("Permission denied: pipeline.edit") does not, so it keeps
    // the plain sentence.
    if (typeof detail === 'string' && detail.trim() && !/^(Permission denied|Module not included)/.test(detail)) {
      return `This change was not saved. ${detail}`
    }
    return 'You do not have permission to edit this deal. Nothing was changed.'
  }
  if (status === 404) return 'This deal is no longer in the pipeline book. Nothing was changed.'

  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        const entry = item as { loc?: unknown[]; msg?: string }
        // loc is ["body", "<field>"]; the field is the last element.
        const field = Array.isArray(entry.loc) ? String(entry.loc[entry.loc.length - 1] ?? '') : ''
        const label = field && field !== 'body' ? `${LOAN_FIELD_LABELS[field] ?? field}: ` : ''
        return entry.msg ? `${label}${entry.msg}` : null
      })
      .filter((part): part is string => Boolean(part))
    if (parts.length > 0) return `This change was not saved. ${parts.join(' · ')}`
  }
  if (typeof detail === 'string') return `This change was not saved. ${detail}`
  // A structured refusal carries its own sentence: typing over a value the
  // engine derived is refused (R1) with `{code: 'derived_output', message}`
  // naming the inputs to correct — "(422)" alone hid what to do instead.
  const structured = (detail as { message?: unknown } | null | undefined)?.message
  if (typeof structured === 'string' && structured.trim()) {
    return `This change was not saved. ${structured}`
  }
  if (status) return `This change was not saved (${status}). Nothing on the deal has changed.`
  return 'This change could not be saved. Check your connection — nothing on the deal has changed.'
}

/** The names the deal screens use, so a validation error names the field a
 *  person just typed in rather than the wire key. */
const LOAN_FIELD_LABELS: Record<string, string> = {
  property: 'Property',
  borrower: 'Borrower',
  sponsor: 'Sponsor',
  location: 'Location',
  assignee: 'Assignee',
  originated: 'Received',
  brokerName: 'Broker',
  broker_name: 'Broker',
  rate: 'Rate',
  loanAmount: 'Amount',
  loan_amount: 'Amount',
  ltv: 'LTV',
  dscr: 'DSCR',
  priority: 'Priority',
}
